import type { ChartType } from '../../PageConfigProvider/types/chart';
import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { DimensionSource } from '../../PageConfigProvider/types/item';
import type { CompositionMetricValues } from '../../PageConfigProvider/types/metric';
import type { SupportedSecondaryDimensions } from '../TopNDataProvider';

// Kept free of React and barrels: the chart table-columns migration bundles
// this for mongosh through `plot-table-columns.ts`.

type TopCompanyHeadcountQuery = {
  type: 'TOP_N';
  segment: SupportedSecondaryDimensions;
};

const TOP_N_SECONDARY_DIMENSIONS = new Set<SupportedSecondaryDimensions>([
  'geography',
  'role',
]);

const isTopNSecondaryDimension = (
  dimensionSource: DimensionSource
): dimensionSource is SupportedSecondaryDimensions =>
  TOP_N_SECONDARY_DIMENSIONS.has(
    dimensionSource as SupportedSecondaryDimensions
  );

type TopHeadcountQuery = {
  type: 'TOP_HEADCOUNT';
  segment: Exclude<
    SecondaryDimensionValues,
    'company' | 'timeseries' | 'subsidiaries'
  >;
};

type StandardQuery = {
  type: 'STANDARD';
};

export type CompositionQuery =
  | TopCompanyHeadcountQuery
  | TopHeadcountQuery
  | StandardQuery;

/**
 * The query a composition chart runs. The top-N queries rank the source's
 * segments across the page's entities and ignore `secondaryDimension`; the
 * standard one breaks the entities down by it and ignores the source.
 */
export const getCompositionQueryType = ({
  chartType,
  dimensionSource,
  metric,
}: {
  chartType: ChartType | undefined;
  dimensionSource: DimensionSource | undefined;
  metric: CompositionMetricValues;
}): CompositionQuery => {
  if (chartType !== 'bar' && chartType !== 'table') {
    return { type: 'STANDARD' };
  }

  if (dimensionSource && dimensionSource !== 'primary') {
    if (dimensionSource === 'competitors') {
      return { type: 'TOP_HEADCOUNT', segment: 'competitors' };
    }
    if (dimensionSource === 'company') {
      return { type: 'TOP_N', segment: 'company' };
    }
    if (isTopNSecondaryDimension(dimensionSource) && metric !== 'headcount') {
      return { type: 'TOP_N', segment: dimensionSource };
    }
    if (
      dimensionSource !== 'timeseries' &&
      dimensionSource !== 'subsidiaries' &&
      metric === 'headcount'
    ) {
      return { type: 'TOP_HEADCOUNT', segment: dimensionSource };
    }
  }

  return { type: 'STANDARD' };
};
